import { useCallback, useEffect, useMemo, useState } from 'react';

import { useProviderServiceDestinationsRetrieveQuery } from '@/services/api/generated/providerDestinationsService';
import type {
  DestinationScope,
  ScopeDestinationBlock,
} from '@/types/provider-destinations';
import { ResearchStatus } from '@/types/provider-destinations';

interface UseDestinationsResult {
  scopes: ScopeDestinationBlock[];
  getScopeDestinations: (
    scope: DestinationScope,
  ) => ScopeDestinationBlock | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export const useDestinations = (
  providerServiceId: string,
): UseDestinationsResult => {
  const [pollingInterval, setPollingInterval] = useState<number | undefined>(
    undefined,
  );

  const { data, isLoading, isError, refetch } =
    useProviderServiceDestinationsRetrieveQuery(
      { providerServiceId },
      {
        skip: !providerServiceId,
        refetchOnFocus: true,
        pollingInterval,
      },
    );

  useEffect(() => {
    const hasResearch = data?.scopes?.some(
      (s) => s.research_status === ResearchStatus.IN_PROGRESS,
    );
    setPollingInterval(hasResearch ? 5000 : undefined);
  }, [data?.scopes]);

  const scopes = useMemo(() => data?.scopes ?? [], [data?.scopes]);

  const getScopeDestinations = useCallback(
    (scope: DestinationScope): ScopeDestinationBlock | undefined =>
      scopes.find((s) => s.scope === scope),
    [scopes],
  );

  return { scopes, getScopeDestinations, isLoading, isError, refetch };
};
